<template>
  <div class="auth">
    <div class="auth__card shadow-card">
      <!-- Brand panel -->
      <div class="auth__brand">
        <div class="auth__brand-inner">
          <img :src="logo" alt="Kairos Afrika" class="auth__logo" />
          <h2 class="auth__brand-title">
            Credit Union<br />Admin Portal
          </h2>
          <p class="auth__brand-sub">
            Manage members, staff, and transactions — all in one place.
          </p>
        </div>
        <div class="auth__glow"></div>
        <div class="auth__glow auth__glow--2"></div>
      </div>

      <!-- Form panel -->
      <div class="auth__form">
        <p class="auth__eyebrow">{{ appTitle }}</p>
        <h1 class="auth__title">Sign in</h1>
        <p class="auth__subtitle">
          Enter your account credentials to continue.
        </p>

        <ValidationObserver v-slot="{ handleSubmit }">
          <v-form @submit.prevent="handleSubmit(logIn)">
            <ValidationProvider v-slot="{ errors }" rules="required">
              <v-text-field
                type="text"
                label="Account Number"
                outlined
                prepend-inner-icon="mdi-account-outline"
                :error-messages="errors[0]"
                autofocus
                color="primary"
                class="auth__field"
                v-model="login.accountNumber"
                @keyup.enter="logIn"
              />
            </ValidationProvider>

            <ValidationProvider v-slot="{ errors }" rules="required">
              <v-text-field
                :type="showPassword ? 'text' : 'password'"
                label="PIN / Password"
                outlined
                prepend-inner-icon="mdi-lock-outline"
                :append-icon="showPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
                @click:append="showPassword = !showPassword"
                :error-messages="errors[0]"
                color="primary"
                class="auth__field"
                v-model="login.password"
                @keyup.enter="logIn"
              />
            </ValidationProvider>

            <div class="auth__row">
              <v-checkbox
                class="auth__remember ma-0 pa-0"
                label="Remember me"
                color="primary"
                hide-details
                v-model="login.remember_me"
              />
            </div>

            <v-btn
              type="submit"
              block
              large
              depressed
              :loading="isLoading"
              :disabled="isLoading"
              color="primary"
              class="auth__submit text-none"
            >
              Sign in
            </v-btn>
          </v-form>
        </ValidationObserver>

        <p class="auth__hint">
          Don't have an account?
          <span>Kindly contact your administrator.</span>
        </p>
      </div>
    </div>

    <SnackBar
      :showSnackBar="showSnackBar"
      :message="message"
      :status="status"
    />
  </div>
</template>

<script>
import { loadComponent } from "@/utils/helpers";
import SnackBarMixins from "../../mixins/SnackBarMIxins";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { extend } from "vee-validate";
import { required } from "vee-validate/dist/rules";
import { mapGetters } from "vuex";
import logo from "@/assets/brand/kairos-afrika-logo.png";

extend("required", {
  ...required,
  message: "This field is required"
});

export default {
  name: "HomePage",
  components: {
    ValidationProvider,
    ValidationObserver,
    SnackBar: loadComponent(`component/SnackBar`)
  },
  computed: {
    ...mapGetters({
      isLoading: "getIsLoading"
    }),
    appTitle() {
      return process.env.VUE_APP_TITLE || "Credit Union";
    }
  },
  data: () => ({
    logo,
    showPassword: false,
    login: {
      accountNumber: "",
      password: "",
      remember_me: false
    }
  }),
  mixins: [SnackBarMixins],
  methods: {
    logIn() {
      this.$store.dispatch("auth/logIn", this.login);
    }
  }
};
</script>

<style scoped>
.auth {
  min-height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: radial-gradient(
      1200px 600px at 100% 0%,
      var(--signal-soft) 0%,
      transparent 55%
    ),
    var(--background);
}

.auth__card {
  display: grid;
  grid-template-columns: 1.05fr 1fr;
  width: 100%;
  max-width: 960px;
  min-height: 560px;
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: calc(var(--radius) * 1.6);
  overflow: hidden;
}

/* Brand panel */
.auth__brand {
  position: relative;
  overflow: hidden;
  background: linear-gradient(160deg, #14181c 0%, #20262c 60%, #2a1420 100%);
  padding: 48px 44px;
  display: flex;
  align-items: flex-start;
}
.auth__brand-inner {
  position: relative;
  z-index: 2;
  margin-top: auto;
  margin-bottom: 8px;
}
.auth__logo {
  width: 190px;
  max-width: 70%;
  height: auto;
  display: block;
  margin-bottom: 28px;
}
.auth__brand-title {
  font-family: var(--font-heading);
  color: #ffffff;
  font-size: 30px;
  line-height: 1.15;
  font-weight: 600;
  letter-spacing: -0.01em;
  margin: 0 0 14px;
}
.auth__brand-sub {
  color: rgba(246, 247, 249, 0.7);
  font-size: 15px;
  line-height: 1.5;
  max-width: 320px;
  margin: 0;
}
.auth__glow {
  position: absolute;
  width: 340px;
  height: 340px;
  border-radius: 50%;
  filter: blur(60px);
  background: var(--signal);
  opacity: 0.35;
  top: -120px;
  right: -100px;
  z-index: 1;
}
.auth__glow--2 {
  width: 220px;
  height: 220px;
  opacity: 0.18;
  top: auto;
  bottom: -90px;
  right: 40%;
}

/* Form panel */
.auth__form {
  padding: 56px 52px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  background: var(--card);
}
.auth__eyebrow {
  text-transform: uppercase;
  letter-spacing: 0.14em;
  font-size: 12px;
  font-weight: 600;
  color: var(--signal);
  margin: 0 0 10px;
}
.auth__title {
  font-family: var(--font-heading);
  font-size: 30px;
  font-weight: 600;
  color: var(--foreground);
  margin: 0 0 6px;
}
.auth__subtitle {
  color: var(--muted-foreground);
  font-size: 14px;
  margin: 0 0 28px;
}
.auth__field {
  margin-bottom: 4px;
}
.auth__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 2px 0 20px;
}
.auth__remember >>> label {
  font-size: 14px;
  color: var(--muted-foreground);
}
.auth__submit {
  height: 50px !important;
  border-radius: var(--radius) !important;
  font-weight: 600;
  letter-spacing: 0.01em;
}
.auth__hint {
  margin: 26px 0 0;
  font-size: 13px;
  color: var(--muted-foreground);
  text-align: center;
}
.auth__hint span {
  color: var(--foreground);
  font-weight: 500;
}

@media (max-width: 860px) {
  .auth__card {
    grid-template-columns: 1fr;
    max-width: 460px;
    min-height: 0;
  }
  .auth__brand {
    padding: 36px 32px;
    min-height: 220px;
  }
  .auth__brand-inner {
    margin-top: 0;
  }
  .auth__form {
    padding: 36px 32px 40px;
  }
}
</style>
